/**
 * Capital d'ouverture + immobilisations — hors solde caisse tant que non épuisé / sans ventes.
 */

import { toMroAmount } from './posCurrencyUtils.js';
import { isSetupPhase, resolveLiquidOutflowPortions } from './fundingSource.js';

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Storage is MRO-base (ADR currency). Default MRO when currency missing —
 * treating bare amounts as MRU multiplied equity/cash by 10 and broke the bilan.
 */
export function normalizeOpeningCapital(raw, currencyType = 'MRO') {
  const src = raw && typeof raw === 'object' ? raw : {};
  const amount = Math.max(0, toNumber(src.amount));
  return {
    amount,
    currency: src.currency || currencyType || 'MRO',
    createdAt: src.createdAt || null,
    description: String(src.description || '').trim()
  };
}

export function normalizeFixedAssetsList(raw, currencyType = 'MRO') {
  const list = Array.isArray(raw) ? raw : [];
  const allowed = new Set(['vehicle', 'furniture', 'building', 'equipment', 'other']);
  return list
    .map((item, index) => {
      if (!item || typeof item !== 'object') return null;
      const amount = Math.max(0, toNumber(item.amount));
      if (amount <= 0 && !String(item.name || '').trim()) return null;
      const category = allowed.has(item.category) ? item.category : 'other';
      return {
        id: item.id || `fa_${index}`,
        name: String(item.name || '').trim(),
        category,
        amount,
        currency: item.currency || currencyType || 'MRO',
        acquiredAt: item.acquiredAt || null,
        description: String(item.description || '').trim()
      };
    })
    .filter(Boolean);
}

export function getOpeningCapitalAmount(openingCapital) {
  const normalized = normalizeOpeningCapital(openingCapital);
  // Convert to MRO (base currency) for central calculation
  return toMroAmount(normalized.amount, normalized.currency);
}

export function getFixedAssetsTotal(fixedAssets) {
  return normalizeFixedAssetsList(fixedAssets).reduce((sum, a) => {
    // Convert to MRO (base currency)
    return sum + toMroAmount(a.amount, a.currency);
  }, 0);
}

/**
 * Immobilisations bilancielles depuis la liste Settings.
 */
export function mapFixedAssetsToBalanceSheet(fixedAssets) {
  const mapped = {
    property: 0,
    equipment: 0,
    vehicles: 0,
    other: 0
  };
  for (const asset of normalizeFixedAssetsList(fixedAssets)) {
    const amountMro = toMroAmount(asset.amount, asset.currency);
    if (asset.category === 'building') mapped.property += amountMro;
    else if (asset.category === 'vehicle') mapped.vehicles += amountMro;
    else if (asset.category === 'equipment' || asset.category === 'furniture') {
      mapped.equipment += amountMro;
    } else mapped.other += amountMro;
  }
  return mapped;
}

/**
 * Trésorerie d'ouverture encore disponible (capital − immobilisations − paiements setup).
 * Tant que > 0, ces sorties ne doivent pas toucher le solde dashboard.
 * Spent = same portions as liquid/Attendu (explicit tags + installation remapping).
 */
export function getRemainingOpeningCapitalCash({
  openingCapital,
  fixedAssets = [],
  setupOutflows = [],
  sales = null,
  installationPhase = null
} = {}) {
  const capital = getOpeningCapitalAmount(openingCapital);
  if (capital <= 0) return 0;
  const fixed = getFixedAssetsTotal(fixedAssets);
  const forceOpening =
    installationPhase != null
      ? Boolean(installationPhase)
      : sales != null
        ? isSetupPhase({ sales })
        : false;
  const spent = (Array.isArray(setupOutflows) ? setupOutflows : []).reduce((sum, entry) => {
    if (!entry) return sum;
    return sum + resolveLiquidOutflowPortions(entry, { installationPhase: forceOpening }).openingCapitalAmt;
  }, 0);
  return Math.max(0, capital - fixed - spent);
}

export function hasActiveOpeningCapital(openingCapital) {
  return getOpeningCapitalAmount(openingCapital) > 0;
}
